/**
 * This is the modal dialog that handles social sharing for scratchpads.
 */
import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import classNames from "classnames";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Checkbox} from "@khanacademy/wonder-blocks-form";
import {OnePaneDialog} from "@khanacademy/wonder-blocks-modal";
import {encodeParams} from "@khan/data-fetch";
import {toAbsoluteUrl} from "@khan/urls";

import type {Scratchpad} from "#scratchpad-base-package/scratchpads.ts";
// eslint-disable-next-line no-restricted-imports -- TODO: Replace this component with the equivalent one from Wonder Blocks.
import DeprecatedLabeledField from "#shared-components-package/deprecated-labeled-field.tsx";
import KUITextArea from "#shared-components-package/labeled-textarea.tsx";
import Social from "#shared-package/social.ts";

import emailIcon from "@phosphor-icons/core/regular/envelope-simple.svg";
import facebookIcon from "@phosphor-icons/core/regular/facebook-logo.svg";
import twitterIcon from "@phosphor-icons/core/regular/twitter-logo.svg";

type Props = {
    // A function to call to determine if there have been any unsaved
    // changes to the contents of the scratchpad
    hasUnsavedChanges: () => boolean;
    // A reference to the current scratchpad object
    // TODO(ian): It's possible that we might want to expand and refactor
    // this shape definition if we need it again.
    // @ts-expect-error - TS2749 - 'Scratchpad' refers to a value, but is being used as a type here. Did you mean 'typeof Scratchpad'?
    scratchpad: Scratchpad;
};

type State = {
    hideAuthorAndPlayback: boolean;
    hideButtons: boolean;
    hideEditor: boolean;
};

class ScratchpadShareModal extends React.Component<Props, State> {
    state: State = {
        hideAuthorAndPlayback: false,
        hideButtons: false,
        hideEditor: false,
    };

    componentDidMount() {
        Social.prepFacebook();
    }

    toggleEmbedButtons: () => void = () => {
        this.setState({hideButtons: !this.state.hideButtons});
    };

    toggleEmbedEditor: () => void = () => {
        this.setState({hideEditor: !this.state.hideEditor});
    };

    toggleEmbedAuthorAndPlayback: () => void = () => {
        this.setState({
            hideAuthorAndPlayback: !this.state.hideAuthorAndPlayback,
        });
    };

    handleFacebookClick: () => void = () => {
        shareHandlers.facebook(this.props.scratchpad);
    };

    handleTwitterClick: () => void = () => {
        shareHandlers.twitter(this.props.scratchpad);
    };

    handleEmailClick: () => void = () => {
        shareHandlers.email(this.props.scratchpad);
    };

    render(): React.ReactNode {
        const unsavedWarning = (
            <p className={classNames("alert", css(styles.unsavedAlert))}>
                {t`Warning: there are un-saved changes in this project.`}
            </p>
        );
        const children = (
            <form>
                {this.props.hasUnsavedChanges() && unsavedWarning}
                <DeprecatedLabeledField
                    disabled={true}
                    title={t`Share this project by using this link:`}
                    type="text"
                    value={getShareURL(this.props.scratchpad)}
                />
                <View style={styles.shareButtonGroup}>
                    <Button
                        aria-label={t`Share this on Facebook`}
                        startIcon={facebookIcon}
                        onClick={this.handleFacebookClick}
                        kind="secondary"
                        style={styles.marginRight}
                    >
                        {t`Share`}
                    </Button>
                    <Button
                        aria-label={t`Share this on Twitter`}
                        startIcon={twitterIcon}
                        onClick={this.handleTwitterClick}
                        kind="secondary"
                        style={styles.marginRight}
                    >
                        {t`Tweet`}
                    </Button>
                    <Button
                        aria-label={t`Share this via email`}
                        startIcon={emailIcon}
                        onClick={this.handleEmailClick}
                        kind="secondary"
                        style={styles.marginRight}
                    >
                        {t`Email`}
                    </Button>
                </View>
                <KUITextArea
                    disabled={true}
                    height="150px"
                    title={t`Have a website? Embed this project on your site.`}
                    value={getShareEmbed(this.props.scratchpad, this.state)}
                />
                <View style={styles.checkboxGroup}>
                    <Checkbox
                        label={t`Hide editor`}
                        checked={this.state.hideEditor}
                        onChange={this.toggleEmbedEditor}
                        style={styles.marginRight}
                    />
                    <Checkbox
                        label={t`Hide buttons`}
                        checked={this.state.hideButtons}
                        onChange={this.toggleEmbedButtons}
                        style={styles.marginRight}
                    />
                    <Checkbox
                        label={t`Hide author and playback`}
                        checked={this.state.hideAuthorAndPlayback}
                        onChange={this.toggleEmbedAuthorAndPlayback}
                        style={styles.marginRight}
                    />
                </View>
            </form>
        );
        return (
            <OnePaneDialog
                title={t`Share this Project`}
                content={<View>{children}</View>}
                style={styles.modal}
            />
        );
    }
}

const styles = StyleSheet.create({
    modal: {
        maxHeight: 500,
    },
    marginRight: {
        marginRight: 16,
    },
    checkboxGroup: {
        flexDirection: "row",
    },
    shareButtonGroup: {
        flexDirection: "row",
        marginBottom: 20,
    },
    unsavedAlert: {
        margin: 0,
        marginBottom: 10,
    },
});

/**
 * Returns commonly used data for sharing via facebook, twitter and email
 */
// @ts-expect-error - TS2749 - 'Scratchpad' refers to a value, but is being used as a type here. Did you mean 'typeof Scratchpad'?
const getShareInfo = (scratchpad: Scratchpad, khanAcademy?: string) => {
    const translatedTitle = scratchpad.get("translatedTitle");
    let message;
    khanAcademy = khanAcademy || t`Khan Academy`;
    if (scratchpad.isOwner()) {
        message = t`I just created "${translatedTitle}" on ${khanAcademy}!`;
    } else {
        message = t`I just explored "${translatedTitle}" on ${khanAcademy}!`;
    }
    return {
        message: message,
        url: getShareURL(scratchpad),
        imageUrl: scratchpad.urlForImage(),
    };
};

/**
 * Returns a shareable URL for this project
 */
// @ts-expect-error - TS2749 - 'Scratchpad' refers to a value, but is being used as a type here. Did you mean 'typeof Scratchpad'?
const getShareURL = (scratchpad: Scratchpad) => {
    return scratchpad.inTutorial()
        ? toAbsoluteUrl(scratchpad.get("defaultUrlPath"))
        : scratchpad.urlForShow();
};

/**
 * Returns embed code for the embed box
 */
// @ts-expect-error - TS2749 - 'Scratchpad' refers to a value, but is being used as a type here. Did you mean 'typeof Scratchpad'?
const getShareEmbed = (scratchpad: Scratchpad, visibilityOptions: State) => {
    const shareURL = getShareURL(scratchpad);
    const isComputerProgramming = shareURL.indexOf("computer-programming") >= 0;
    const translatedTitle = scratchpad.get("translatedTitle");
    const urlParams = {
        editor: !visibilityOptions.hideEditor,
        buttons: !visibilityOptions.hideButtons,
        author: !visibilityOptions.hideAuthorAndPlayback,
        embed: true,
    } as const;

    const humanParams: Record<string, "yes" | "no"> = {};
    for (const key in urlParams) {
        if (urlParams.hasOwnProperty(key)) {
            humanParams[key] = urlParams[key] ? "yes" : "no";
        }
    }

    const contentKind = scratchpad.get("contentKind");

    let url;
    // To get an embeddable link for new-style "Scratchpad"-like content kinds,
    // we need to perform URL surgery.
    if (contentKind === "Scratchpad") {
        url = scratchpad.urlForShow();
    } else {
        const defaultUrlPath = scratchpad.get("defaultUrlPath");
        // We extract the subject slug for this content.
        const subjectSlug = defaultUrlPath.split("/")[2];
        // And form the URL: http(s)://<host>/<subject>/<slug>/<kind>:<id>
        url = shareURL.replace(
            defaultUrlPath,
            `/${subjectSlug}/${scratchpad.get("slug")}` +
                `/${contentKind}:${scratchpad.get("id")}`,
        );
    }

    const embedURL = `${url}/embed.js?${encodeParams(humanParams)}`;
    // We Nolint here so we don't have to wrap this in the safeLinkTo
    // URL wrapper, which we don't want in this case.
    const csUrl = "http://www.khanacademy.org/computer-programming";
    const csTitle = "Khan Academy Computer Science";

    const computerProgrammingInfo = t`<p>Made using: <a href="${csUrl}">${csTitle}</a></p>`;

    const safeTitle = translatedTitle
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");

    return (
        `<h2>` +
        `<a href="${shareURL}">${safeTitle}</a>` +
        `</h2>` +
        `<script src="${embedURL}"></script>` +
        (isComputerProgramming ? computerProgrammingInfo : "")
    );
};

/* Handlers for sharing to various social platforms */
const shareHandlers = {
    // @ts-expect-error - TS2749 - 'Scratchpad' refers to a value, but is being used as a type here. Did you mean 'typeof Scratchpad'?
    facebook(scratchpad: Scratchpad) {
        const shareInfo = getShareInfo(scratchpad);
        Social.facebookShare(
            shareInfo.message,
            shareInfo.url,
            shareInfo.imageUrl,
        );
    },
    // @ts-expect-error - TS2749 - 'Scratchpad' refers to a value, but is being used as a type here. Did you mean 'typeof Scratchpad'?
    twitter(scratchpad: Scratchpad) {
        const shareInfo = getShareInfo(scratchpad, "@khanacademy");
        Social.openTwitterPopup(
            Social.formatTwitterShareUrl({
                url: shareInfo.url,
                text: shareInfo.message,
            }),
        );
    },
    // @ts-expect-error - TS2749 - 'Scratchpad' refers to a value, but is being used as a type here. Did you mean 'typeof Scratchpad'?
    email(scratchpad: Scratchpad) {
        const shareInfo = getShareInfo(scratchpad);
        const emailBody = t`Check it out at ${shareInfo.url}.`;
        window.open(
            Social.formatMailtoUrl({
                subject: shareInfo.message,
                body: emailBody,
            }),
            "_newtab",
        );
    },
} as const;

export default ScratchpadShareModal;
